// src/App.js
import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress, Typography, Stack } from '@mui/material';
import { Business } from '@mui/icons-material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Theme
import createAppTheme from './utils/theme';

// Services
import AuthService from './services/authService';
import { enrichUserWithProfile } from './utils/resolveUserId';


// Layout
import MainLayout from './components/common/Layout/MainLayout';

// Pages
import Dashboard from './pages/Dashboard/Dashboard';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import Credits from './pages/Credits/Credits';
import NewCredit from './pages/Credits/NewCredit';
import Approvals from './pages/Approvals/Approvals';
import DefineLimit from './pages/Admin/DefineLimit';
import ViewLimits from './pages/Admin/ViewLimits';
import PendingLimitRequests from './pages/Admin/PendingLimitRequests';
import AllUsers from './pages/Admin/AllUsers';
import RiskManagement from './pages/Admin/RiskManagement';
import InterestRateManagement from './pages/Admin/InterestRateManagement';
import DiscountManagement from './pages/Admin/DiscountManagement';
import CreateDiscount from './pages/User/CreateDiscount';
import DiscountList from './pages/User/DiscountList';
import InvoiceApproval from './pages/User/InvoiceApproval';
import Profile from './pages/Profile/Profile';
import ChangePassword from './pages/Profile/ChangePassword';
import EdefterUpload from './pages/Edefter/EdefterUpload';
import TrialBalance from './pages/Edefter/TrialBalance';
import EDashboard from './pages/Edefter/EDashboard';
import MizanReading from './pages/Edefter/MizanReading';
import AddCompany from './pages/Edefter/AddCompany';

const PLACEHOLDER_USER = Object.freeze({
  name: 'User',
  role: 'User',
  email: 'user@example.com',
});

const PLACEHOLDER_ADMIN = Object.freeze({
  name: 'Admin',
  role: 'Admin',
  email: 'admin@example.com',
});

// Protected Route Component - Redirects unauthenticated users to login
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = AuthService.isAuthenticated();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route Component - Only allows Admin users
const AdminRoute = ({ children }) => {
  const isAuthenticated = AuthService.isAuthenticated();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!AuthService.hasRole('Admin')) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Auth Route Component (authenticated users should not access login/register/forgot-password - redirects authenticated users from login, forgot password and register to dashboard)
const AuthRoute = ({ children }) => {
  const isAuthenticated = AuthService.isAuthenticated();
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [bootstrapped, setBootstrapped] = useState(false);
  const appTheme = useMemo(
    () => createAppTheme(user?.role || 'User'),
    [user?.role]
  );

  const resolveUser = async () => {
    const currentUser = AuthService.getCurrentUser();
    if (!currentUser) {
      return null;
    }

    const enriched = await enrichUserWithProfile(currentUser);
    if (enriched?.userId != null && enriched.userId !== currentUser.userId) {
      AuthService.updateUserData(enriched);
    }

    return enriched;
  };

  // Update user information
  const updateUser = async () => {
    const resolvedUser = await resolveUser();
    setUser(resolvedUser);
    setBootstrapped(true);
  };

  // Global toast function
  useEffect(() => {
    window.showToast = (message, type = 'info') => {
      const toastOptions = {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      };

      switch (type) {
        case 'success':
          toast.success(message, toastOptions);
          break;
        case 'error':
          toast.error(message, toastOptions);
          break;
        case 'warning':
          toast.warning(message, toastOptions);
          break;
        default:
          toast.info(message, toastOptions);
      }
    };

    return () => {
      delete window.showToast;
    };
  }, []);

  // Update document title and favicon
  useEffect(() => {
    // Update document title
    document.title = 'DPG Finance';
    
    // Update favicon dynamically
    let link = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = '/favicon.ico';
    link.type = 'image/x-icon';
    
    // Update apple-touch-icon
    let appleLink = document.querySelector("link[rel*='apple-touch-icon']");
    if (!appleLink) {
      appleLink = document.createElement('link');
      appleLink.rel = 'apple-touch-icon';
      document.getElementsByTagName('head')[0].appendChild(appleLink);
    }
    appleLink.href = '/favicon.ico';
  }, []);

  // Get user information on initial load and listen to login/logout events
  useEffect(() => {
    updateUser();

    // Listen to login event
    const handleUserLogin = async () => {
      const currentUser = AuthService.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
      }
      await updateUser();
    };

    // Listen to logout event
    const handleUserLogout = () => {
      setUser(null);
    };

    window.addEventListener('userLogin', handleUserLogin);
    window.addEventListener('userLogout', handleUserLogout);

    return () => {
      window.removeEventListener('userLogin', handleUserLogin);
      window.removeEventListener('userLogout', handleUserLogout);
    };
  }, []);

  // Session timeout check
  useEffect(() => {
    // Session check every 5 minutes - token is checked every 5 minutes, if it expires, authService.logout() is called.
    const interval = setInterval(() => {
      if (AuthService.isAuthenticated()) {
        AuthService.checkSessionTimeout();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, []);

  // Show loading until auth bootstrap completes
  if (!bootstrapped) {
    return (
      <ThemeProvider theme={appTheme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
            }
          }}
        >
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              textAlign: 'center',
              px: { xs: 3, sm: 4 },
            }}
          >
            <Stack spacing={4} alignItems="center">
              {/* Logo */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    animation: 'pulse 2s ease-in-out infinite',
                  }}
                >
                  <Business sx={{ fontSize: { xs: 48, sm: 64 }, color: 'white' }} />
                </Box>
              </Box>

              {/* Spinner */}
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress
                  size={60}
                  thickness={4}
                  sx={{
                    color: 'white',
                    animationDuration: '1.5s',
                  }}
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(5px)',
                    }}
                  />
                </Box>
              </Box>

              {/* Text */}
              <Stack spacing={1} alignItems="center">
                <Typography
                  variant="h5"
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  }}
                >
                  DPG Finance
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    maxWidth: 300,
                  }}
                >
                  Loading...
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <Router>
        <Routes>
          {/* Auth Routes */}
          <Route 
            path="/login" 
            element={
              <AuthRoute>
                <Login />
              </AuthRoute>
            } 
          />
          <Route 
            path="/register" 
            element={
              <AuthRoute>
                <Register />
              </AuthRoute>
            } 
          />
          <Route 
            path="/forgot-password" 
            element={
              <AuthRoute>
                <ForgotPassword />
              </AuthRoute>
            } 
          />

          {/* Protected Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <MainLayout user={user || PLACEHOLDER_USER}>
                  <Dashboard user={user || PLACEHOLDER_USER} />
                </MainLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/user/credits" 
            element={
              <ProtectedRoute>
                <MainLayout user={user || PLACEHOLDER_USER}>
                  <Credits user={user || PLACEHOLDER_USER} />
                </MainLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/user/new-credit" 
            element={
              <ProtectedRoute>
                <MainLayout user={user || PLACEHOLDER_USER}>
                  <NewCredit user={user || PLACEHOLDER_USER} />
                </MainLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/user/approvals" 
            element={
              <ProtectedRoute>
                <MainLayout user={user || PLACEHOLDER_USER}>
                  <Approvals user={user || PLACEHOLDER_USER} />
                </MainLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/user/discount/create" 
            element={
              <ProtectedRoute>
                <MainLayout user={user || PLACEHOLDER_USER}>
                  <CreateDiscount user={user || PLACEHOLDER_USER} />
                </MainLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/user/discount/list" 
            element={
              <ProtectedRoute>
                <MainLayout user={user || PLACEHOLDER_USER}>
                  <DiscountList user={user || PLACEHOLDER_USER} />
                </MainLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/user/invoice-approval" 
            element={
              <ProtectedRoute>
                <MainLayout user={user || PLACEHOLDER_USER}>
                  <InvoiceApproval user={user || PLACEHOLDER_USER} />
                </MainLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/edefter/dashboard" 
            element={
              <AdminRoute>
                <MainLayout user={user || PLACEHOLDER_ADMIN}>
                  <EDashboard />
                </MainLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/edefter/upload" 
            element={
              <AdminRoute>
                <MainLayout user={user || PLACEHOLDER_ADMIN}>
                  <EdefterUpload />
                </MainLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/edefter/trial-balance" 
            element={
              <AdminRoute>
                <MainLayout user={user || PLACEHOLDER_ADMIN}>
                  <TrialBalance />
                </MainLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/edefter/mizan-reading" 
            element={
              <AdminRoute>
                <MainLayout user={user || PLACEHOLDER_ADMIN}>
                  <MizanReading />
                </MainLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/edefter/add-company" 
            element={
              <AdminRoute>
                <MainLayout user={user || PLACEHOLDER_ADMIN}>
                  <AddCompany />
                </MainLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <MainLayout user={user || PLACEHOLDER_USER}>
                  <Profile user={user || PLACEHOLDER_USER} />
                </MainLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/change-password" 
            element={
              <ProtectedRoute>
                <MainLayout user={user || PLACEHOLDER_USER}>
                  <ChangePassword />
                </MainLayout>
              </ProtectedRoute>
            } 
          />
          

          {/* Admin Routes */}
          <Route 
            path="/admin/limits/define" 
            element={
              <AdminRoute>
                <MainLayout user={user || PLACEHOLDER_ADMIN}>
                  <DefineLimit user={user || PLACEHOLDER_ADMIN} />
                </MainLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/limits/pending"
            element={
              <AdminRoute>
                <MainLayout user={user || PLACEHOLDER_ADMIN}>
                  <PendingLimitRequests user={user || PLACEHOLDER_ADMIN} />
                </MainLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/limits/view" 
            element={
              <AdminRoute>
                <MainLayout user={user || PLACEHOLDER_ADMIN}>
                  <ViewLimits user={user || PLACEHOLDER_ADMIN} />
                </MainLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/users/all" 
            element={
              <AdminRoute>
                <MainLayout user={user || PLACEHOLDER_ADMIN}>
                  <AllUsers user={user || PLACEHOLDER_ADMIN} />
                </MainLayout>
              </AdminRoute>
            } 
          />

          <Route 
            path="/admin/risks" 
            element={
              <AdminRoute>
                <MainLayout user={user || PLACEHOLDER_ADMIN}>
                  <RiskManagement user={user || PLACEHOLDER_ADMIN} />
                </MainLayout>
              </AdminRoute>
            } 
          />

          <Route 
            path="/admin/interest-rates" 
            element={
              <AdminRoute>
                <MainLayout user={user || PLACEHOLDER_ADMIN}>
                  <InterestRateManagement user={user || PLACEHOLDER_ADMIN} />
                </MainLayout>
              </AdminRoute>
            } 
          />

          <Route 
            path="/admin/discounts" 
            element={
              <AdminRoute>
                <MainLayout user={user || PLACEHOLDER_ADMIN}>
                  <DiscountManagement user={user || PLACEHOLDER_ADMIN} />
                </MainLayout>
              </AdminRoute>
            } 
          />

          {/* Default Route */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          
          {/* 404 Route */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
      
      {/* Toast Notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </ThemeProvider>
  );
}

export default App;
